import { useEffect, useMemo, useState } from "react";

import {
  createBoard,
  BoardLimitError,
  deleteBoard,
  listBoards,
  renameBoard,
  loadBoard,
  type Board,
} from "../data/boards";
import { getCurrentUser, type UserProfile } from "../data/auth";

import { MiBoardDialog } from "./MiBoardDialog";
import { InternalSidebar } from "./InternalSidebar";
import { LoadingClock } from "./LoadingClock";

export const BoardList = () => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [deletingBoardId, setDeletingBoardId] = useState<string | null>(null);
  const [duplicatingBoardId, setDuplicatingBoardId] = useState<string | null>(
    null,
  );
  const [boardPendingDuplicate, setBoardPendingDuplicate] =
    useState<Board | null>(null);
  const [boardPendingDuplicateRename, setBoardPendingDuplicateRename] =
    useState<Board | null>(null);
  const [duplicateTitle, setDuplicateTitle] = useState("");
  const [boardPendingRename, setBoardPendingRename] = useState<Board | null>(
    null,
  );
  const [renameTitle, setRenameTitle] = useState("");
  const [boardPendingDeletion, setBoardPendingDeletion] =
    useState<Board | null>(null);
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [subscription, setSubscription] = useState<
    UserProfile["subscription"] | null
  >(null);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlHeight = html.style.height;
    const previousBodyHeight = body.style.height;

    html.style.overflow = "auto";
    body.style.overflow = "auto";
    html.style.height = "auto";
    body.style.height = "auto";

    Promise.all([listBoards(), getCurrentUser()])
      .then(([loadedBoards, user]) => {
        setBoards(loadedBoards);
        setSubscription(user.subscription);
      })
      .catch((reason) => {
        if (
          reason instanceof Error &&
          reason.message.includes("Sessão expirada")
        ) {
          window.location.assign(
            `/app/login?next=${encodeURIComponent("/app/boards")}`,
          );
          return;
        }
        setError("Não foi possível carregar os painéis.");
      })
      .finally(() => setLoading(false));

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      html.style.height = previousHtmlHeight;
      body.style.height = previousBodyHeight;
    };
  }, []);

  const filtered = useMemo(
    () =>
      boards.filter((board) =>
        board.title.toLowerCase().includes(query.toLowerCase()),
      ),
    [boards, query],
  );
  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageBoards = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [currentPage, filtered],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [query]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <main className="miboard-loading-screen">
        <LoadingClock label="Carregando painéis..." />
      </main>
    );
  }

  const handleDelete = async (board: Board) => {
    setDeletingBoardId(board.id);
    setError("");
    try {
      await deleteBoard(board.id);
      setBoards((currentBoards) =>
        currentBoards.filter((currentBoard) => currentBoard.id !== board.id),
      );
    } catch {
      setError("Não foi possível excluir o painel. Tente novamente.");
    } finally {
      setDeletingBoardId(null);
      setBoardPendingDeletion(null);
    }
  };

  const duplicateBoard = async (board: Board, title: string) => {
    setDuplicatingBoardId(board.id);
    setError("");
    try {
      const source = board.document ? board : await loadBoard(board.id);
      const duplicatedBoard = await createBoard({
        title: title.slice(0, 200),
        document: {
          elements: source.document?.elements || [],
          appState: {
            ...source.document?.appState,
            showWelcomeScreen: false,
          },
        },
      });
      window.location.assign(`/app?board=${duplicatedBoard.id}`);
      return duplicatedBoard;
    } catch (reason) {
      setError(
        reason instanceof BoardLimitError
          ? reason.message
          : "Não foi possível duplicar o painel. Tente novamente.",
      );
      throw new Error("duplicate_failed");
    } finally {
      setDuplicatingBoardId(null);
    }
  };

  const openDuplicateConfirm = (board: Board) => {
    setError("");
    setBoardPendingDuplicate(board);
  };

  const openDuplicateRename = (board: Board) => {
    setError("");
    setDuplicateTitle(`Cópia de ${board.title}`.slice(0, 200));
    setBoardPendingDuplicateRename(board);
  };

  const confirmDuplicate = async () => {
    if (!boardPendingDuplicate) {
      return;
    }
    try {
      await duplicateBoard(
        boardPendingDuplicate,
        `Cópia de ${boardPendingDuplicate.title}`,
      );
      setBoardPendingDuplicate(null);
    } catch {
      // error state already handled in duplicateBoard
    }
  };

  const confirmDuplicateRename = async () => {
    if (!boardPendingDuplicateRename) {
      return;
    }
    setDuplicatingBoardId(boardPendingDuplicateRename.id);
    try {
      const title =
        duplicateTitle.trim() ||
        `Cópia de ${boardPendingDuplicateRename.title}`;
      await duplicateBoard(boardPendingDuplicateRename, title);
      setBoardPendingDuplicateRename(null);
    } catch {
      // error state already handled in duplicateBoard
    }
  };

  const handleCreateBoard = async () => {
    setCreatingBoard(true);
    setError("");
    try {
      window.location.assign("/app");
    } finally {
      setCreatingBoard(false);
    }
  };

  const handleRenameBoard = async () => {
    if (!boardPendingRename) {
      return;
    }
    const title = renameTitle.trim();
    if (!title) {
      setError("O nome do painel não pode ficar em branco.");
      return;
    }
    setDuplicatingBoardId(boardPendingRename.id);
    setError("");
    try {
      const updatedBoard = await renameBoard(boardPendingRename.id, title);
      setBoards((currentBoards) =>
        currentBoards.map((board) =>
          board.id === updatedBoard.id ? updatedBoard : board,
        ),
      );
      setBoardPendingRename(null);
    } catch {
      setError("Não foi possível renomear o painel. Tente novamente.");
    } finally {
      setDuplicatingBoardId(null);
    }
  };

  return (
    <main className="miboard-boardlist-shell">
      <div className="miboard-boardlist-layout">
        <InternalSidebar active="boards" />
        <div className="miboard-boardlist-content">
          <img
            src="/image/Logo%20Miboard.png"
            alt="MiBoard"
            className="miboard-boardlist-logo"
          />
          <div className="miboard-boardlist-header">
            <div>
              <h1 className="miboard-boardlist-title">Meus painéis</h1>
              <p className="miboard-boardlist-subtitle">
                {subscription?.planCode === "premium"
                  ? "Seu plano Premium permite salvar painéis sem limite."
                  : "No plano Basic, você pode salvar 1 painel. Faça upgrade para o Premium e libere painéis ilimitados."}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCreateBoard}
              disabled={creatingBoard}
              className="miboard-boardlist-primaryButton"
            >
              {creatingBoard ? "Criando..." : "Novo painel"}
            </button>
          </div>
          {subscription?.planCode !== "premium" && (
            <aside className="miboard-boardlist-planNotice" role="status">
              <strong>Plano Basic: {subscription?.boardUsage || "0 de 1 painel salvo"}.</strong>{" "}
              Para salvar mais painéis, faça upgrade para o Premium.
            </aside>
          )}
          <input
            aria-label="Buscar painéis"
            placeholder="Buscar painéis"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="miboard-boardlist-search"
          />
          {error && <p className="miboard-boardlist-error">{error}</p>}
          <section className="miboard-boardlist-grid">
            {pageBoards.map((board) => (
              <article key={board.id} className="miboard-boardlist-card">
                <button
                  type="button"
                  onClick={() =>
                    window.location.assign(`/app?board=${board.id}`)
                  }
                  className="miboard-boardlist-cardLink"
                >
                  <strong>{board.title}</strong>
                  <br />
                  <small>
                    Atualizado:{" "}
                    {new Date(board.updatedAt).toLocaleString("pt-BR")}
                  </small>
                </button>
                <button
                  type="button"
                  onClick={() => openDuplicateConfirm(board)}
                  disabled={
                    duplicatingBoardId === board.id ||
                    deletingBoardId === board.id
                  }
                  className="miboard-boardlist-actionButton"
                >
                  {duplicatingBoardId === board.id
                    ? "Duplicando..."
                    : "Duplicar"}
                </button>
                <button
                  type="button"
                  onClick={() => openDuplicateRename(board)}
                  disabled={
                    duplicatingBoardId === board.id ||
                    deletingBoardId === board.id
                  }
                  className="miboard-boardlist-actionButton"
                >
                  Duplicar como...
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    setRenameTitle(board.title);
                    setBoardPendingRename(board);
                  }}
                  disabled={
                    duplicatingBoardId === board.id ||
                    deletingBoardId === board.id
                  }
                  className="miboard-boardlist-actionButton"
                >
                  Renomear
                </button>
                <button
                  type="button"
                  onClick={() => setBoardPendingDeletion(board)}
                  disabled={deletingBoardId === board.id}
                  className="miboard-boardlist-actionButton miboard-boardlist-actionButton--danger"
                >
                  {deletingBoardId === board.id ? "Excluindo..." : "Excluir"}
                </button>
              </article>
            ))}
          </section>
          {!error && filtered.length === 0 && (
            <p className="miboard-boardlist-empty">Nenhum painel encontrado.</p>
          )}
          {filtered.length > 0 && (
            <div className="miboard-boardlist-pagination">
              <button
                type="button"
                className="miboard-boardlist-pageButton"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              >
                Anterior
              </button>
              <span className="miboard-boardlist-pageInfo">
                Página {currentPage} de {totalPages} · {filtered.length} painéis
              </span>
              <button
                type="button"
                className="miboard-boardlist-pageButton"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
              >
                Próxima
              </button>
            </div>
          )}
          {boardPendingDeletion && (
            <MiBoardDialog
              title="Excluir painel"
              onClose={() => !deletingBoardId && setBoardPendingDeletion(null)}
              actions={
                <>
                  <button
                    type="button"
                    disabled={!!deletingBoardId}
                    onClick={() => setBoardPendingDeletion(null)}
                    className="miboard-cancel-button"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={!!deletingBoardId}
                    onClick={() => handleDelete(boardPendingDeletion)}
                    className="miboard-boardlist-dialogButton miboard-boardlist-dialogButton--danger"
                  >
                    {deletingBoardId ? "Excluindo..." : "Excluir"}
                  </button>
                </>
              }
            >
              <p style={{ margin: 0 }}>
                Excluir o painel “{boardPendingDeletion.title}”? Esta ação não
                poderá ser desfeita.
              </p>
            </MiBoardDialog>
          )}
          {boardPendingDuplicate && (
            <MiBoardDialog
              title="Duplicar painel"
              onClose={() =>
                !duplicatingBoardId && setBoardPendingDuplicate(null)
              }
              actions={
                <>
                  <button
                    type="button"
                    disabled={!!duplicatingBoardId}
                    onClick={() => setBoardPendingDuplicate(null)}
                    className="miboard-cancel-button"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={!!duplicatingBoardId}
                    onClick={confirmDuplicate}
                    className="miboard-boardlist-dialogButton"
                  >
                    {duplicatingBoardId ? "Duplicando..." : "Duplicar"}
                  </button>
                </>
              }
            >
              <p style={{ margin: 0 }}>
                Duplicar o painel “{boardPendingDuplicate.title}” com o nome
                padrão “Cópia de {boardPendingDuplicate.title}”?
              </p>
            </MiBoardDialog>
          )}
          {boardPendingDuplicateRename && (
            <MiBoardDialog
              title="Duplicar como..."
              onClose={() =>
                !duplicatingBoardId && setBoardPendingDuplicateRename(null)
              }
              actions={
                <>
                  <button
                    type="button"
                    disabled={!!duplicatingBoardId}
                    onClick={() => setBoardPendingDuplicateRename(null)}
                    className="miboard-cancel-button"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={!!duplicatingBoardId}
                    onClick={confirmDuplicateRename}
                    className="miboard-boardlist-dialogButton"
                  >
                    {duplicatingBoardId ? "Duplicando..." : "Duplicar"}
                  </button>
                </>
              }
            >
              <label className="miboard-boardlist-dialogField">
                Nome da cópia
                <input
                  autoFocus
                  value={duplicateTitle}
                  onChange={(event) => setDuplicateTitle(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      confirmDuplicateRename();
                    }
                  }}
                  className="miboard-boardlist-dialogInput"
                />
              </label>
              <p className="miboard-boardlist-dialogText">
                A cópia será criada no banco com esse novo nome.
              </p>
            </MiBoardDialog>
          )}
          {boardPendingRename && (
            <MiBoardDialog
              title="Renomear painel"
              onClose={() => !duplicatingBoardId && setBoardPendingRename(null)}
              actions={
                <>
                  <button
                    type="button"
                    disabled={!!duplicatingBoardId}
                    onClick={() => setBoardPendingRename(null)}
                    className="miboard-cancel-button"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={!!duplicatingBoardId}
                    onClick={handleRenameBoard}
                    className="miboard-boardlist-dialogButton"
                  >
                    {duplicatingBoardId ? "Salvando..." : "Salvar"}
                  </button>
                </>
              }
            >
              <label className="miboard-boardlist-dialogField">
                Nome do painel
                <input
                  autoFocus
                  value={renameTitle}
                  onChange={(event) => setRenameTitle(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleRenameBoard();
                    }
                  }}
                  className="miboard-boardlist-dialogInput"
                />
              </label>
              <p className="miboard-boardlist-dialogText">
                O nome novo será salvo no banco imediatamente.
              </p>
            </MiBoardDialog>
          )}
        </div>
      </div>
    </main>
  );
};
